/**
 * DigitalCoworkerDetailsDialog — the destination for the "View Details" action.
 *
 * The menu item shipped without an onClick in all three of its render sites
 * (index.tsx and both card/list menus in DigitalCoworkerV2.tsx) and there was no
 * `/digital-coworker/:id` route, so clicking it only closed the menu (ENG-2766).
 *
 * Everything shown here already comes back from `listDigitalCoworkers`, so this
 * needs no new endpoint and no extra fetch — the record the row was rendered
 * from is the record the dialog reads.
 */

import { IntuiBadge } from '@/components/intueri/IntuiBadge'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiDialog } from '@/components/intueri/IntuiDialog'
import type { DigitalCoworkerRecord } from '@/service/api'
import { useTranslation } from '@/i18n'
import { formatRelativeTime } from '@/utils/timeUtils'
import { coworkerAvatarUrl } from '@/utils/coworkerAvatar'

const getInitials = (name: string) =>
  name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()

const statusVariant = (s: string) => {
  if (s === 'active') return 'success' as const
  if (s === 'training') return 'warning' as const
  return 'secondary' as const
}

interface DigitalCoworkerDetailsDialogProps {
  coworker: DigitalCoworkerRecord | null
  onOpenChange: (open: boolean) => void
}

export function DigitalCoworkerDetailsDialog({
  coworker,
  onOpenChange,
}: DigitalCoworkerDetailsDialogProps) {
  const { t } = useTranslation()

  if (!coworker) return null

  const avatarUrl = coworkerAvatarUrl(coworker)

  const rows: { label: string; value: string }[] = [
    { label: t('digital_coworker:table.role'), value: coworker.job_title || '—' },
    {
      label: t('digital_coworker:new_coworker.department_label'),
      value: coworker.department || '—',
    },
    {
      label: t('digital_coworker:new_coworker.expertise_label'),
      value: coworker.expertise || '—',
    },
    {
      label: t('digital_coworker:table.documents'),
      value: String(coworker.indexed_documents_count),
    },
    {
      label: t('digital_coworker:table.interviews'),
      value: String(coworker.interviews_count),
    },
    {
      label: t('digital_coworker:table.integration'),
      value: coworker.google_integration.is_connected
        ? t('digital_coworker:integration_connected', {
            email: coworker.google_integration.email ?? '',
          })
        : t('digital_coworker:integration_none'),
    },
    {
      label: t('digital_coworker:details.last_synced'),
      value: coworker.last_synced_at
        ? formatRelativeTime(coworker.last_synced_at, t)
        : t('digital_coworker:details.never'),
    },
    {
      label: t('digital_coworker:table.created'),
      value: formatRelativeTime(coworker.created_at, t),
    },
  ]

  return (
    <IntuiDialog
      title={t('digital_coworker:details.title')}
      open
      onOpenChange={onOpenChange}
      size="lg"
      footer={
        <IntuiButton variant="outline" onClick={() => onOpenChange(false)}>
          {t('digital_coworker:add_knowledge.close')}
        </IntuiButton>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-12 w-12 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground">
              {getInitials(coworker.name)}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{coworker.name}</p>
            <p className="truncate text-xs text-muted-foreground">{coworker.job_title}</p>
          </div>
          <IntuiBadge variant={statusVariant(coworker.status)} className="ml-auto shrink-0">
            {t(`digital_coworker:status_filter.${coworker.status}`)}
          </IntuiBadge>
        </div>

        <dl className="grid grid-cols-[minmax(0,9rem)_1fr] gap-x-4 gap-y-2 text-sm">
          {rows.map((row) => (
            <div key={row.label} className="contents">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="min-w-0 break-words">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </IntuiDialog>
  )
}

export default DigitalCoworkerDetailsDialog
